import { useEffect, useRef, useState } from "react";
import { useParams } from "@tanstack/react-router";
import {
  getUser,
  getUsersApi,
  TenantSettingsUpdateError,
  updateTenantSettings,
  updateUser,
} from "@praesto/core";
import type { ChannelId, NotificationPreferences, NotificationType } from "@praesto/shared-types";
import { currentDeviceToken, disablePushOnThisDevice, enablePushOnThisDevice } from "../lib/push";
import { type RegisteredDevice, deviceLabel, removeDevice, subscribeDevices } from "../lib/devices";
import { relativeTime, timestampToMillis } from "../lib/time";
import { ChannelCell } from "./ChannelCell";
import { CONFIGURABLE_NOTIFICATION_TYPES } from "./notification-types";
import {
  type PerType,
  perTypeCellState,
  setPerTypeCell,
  toPerTypeChannels,
} from "./per-type-model";

interface TenantNotif {
  readonly enabled: boolean;
  readonly channels: { readonly email: boolean; readonly push: boolean };
}
const DEFAULT_TENANT_NOTIF: TenantNotif = {
  enabled: true,
  channels: { email: true, push: true },
};
import { Badge, Card, Section, SkeletonFormSection, Switch } from "@praesto/ui-kit";
import { useCore } from "../lib/core";

type SaveStatus = "idle" | "saving" | "saved" | "error";

const DEFAULT_PREFS: NotificationPreferences = {
  channels: { inApp: true, sms: false, whatsapp: false, push: false, email: false },
  perType: {},
};

/**
 * Normalizes whatever `notificationPreferences` a user document actually
 * carries into the full shape this component relies on. Live docs are not
 * guaranteed to have the complete shape — a bare `{}` (written by every user
 * creation path, `functions/src/api/users/index.ts`) and a present-but-empty
 * `{ channels: {} }` both show up in production — and both are truthy, so a
 * plain `raw ?? DEFAULT_PREFS` never falls back and `prefs.channels.email`
 * throws on the missing key. Merging field-by-field over the defaults makes
 * any partial document degrade to defaults instead of crashing the page.
 */
function mergeNotificationPreferences(
  raw: Partial<NotificationPreferences> | undefined,
): NotificationPreferences {
  return {
    channels: { ...DEFAULT_PREFS.channels, ...raw?.channels },
    perType: raw?.perType ?? DEFAULT_PREFS.perType,
    ...(raw?.quietHours ? { quietHours: raw.quietHours } : {}),
  };
}

// Area order follows first-occurrence in the catalogue — same derivation
// `NotificationRulesSettings` (Task 9) uses for the tenant grid, so a Task 8
// catalogue change can't drift the two grids apart.
const PERTYPE_AREAS = Array.from(new Set(CONFIGURABLE_NOTIFICATION_TYPES.map((x) => x.area)));

function StatusBadge({ status }: { status: SaveStatus }) {
  const core = useCore();
  const t = core.i18n.t;
  if (status === "saving")
    return (
      <Badge tone="neutral" variant="soft" size="xs">
        {t("settings.saving")}
      </Badge>
    );
  if (status === "saved")
    return (
      <Badge tone="success" variant="soft" size="xs">
        {t("settings.saved")}
      </Badge>
    );
  if (status === "error")
    return (
      <Badge tone="danger" variant="soft" size="xs">
        {t("settings.error")}
      </Badge>
    );
  return null;
}

/** A labelled toggle row with a 44px tap target. Every toggle in this panel
 *  saves on flip (no Save button), so the DS `Switch` is the right control —
 *  a checkbox here would promise a deferred save that never happens. */
function ToggleRow(props: {
  readonly label: string;
  readonly hint?: string;
  readonly checked: boolean;
  readonly disabled?: boolean;
  readonly onChange?: (checked: boolean) => void;
}) {
  return (
    <label
      className={`flex items-center justify-between gap-3 min-h-touch ${
        props.disabled ? "cursor-not-allowed" : "cursor-pointer"
      }`}
    >
      <span className="flex-1">
        <span className="block text-sm text-fg-default">{props.label}</span>
        {props.hint && <span className="block text-xs text-fg-muted">{props.hint}</span>}
      </span>
      <Switch
        checked={props.checked}
        disabled={props.disabled ?? false}
        onChange={(c) => props.onChange?.(c)}
      />
    </label>
  );
}

/**
 * One `type` cell of the personal per-type grid: computes state/active over
 * the user's own flat `perType` map and delegates rendering to the shared
 * `ChannelCell` (same component `NotificationRuleCell` in
 * `NotificationRulesSettings`, Task 9, uses for the tenant-wide role × type
 * grid). This is the real "overridable default" escape hatch: an explicit
 * per-type override here wins over whatever the tenant's `rules` resolve to
 * for the user's role.
 */
function PersonalTypeCell(props: {
  readonly perType: PerType | undefined;
  readonly type: NotificationType;
  readonly onChange: (next: PerType) => void;
}) {
  const { perType, type, onChange } = props;
  const state = perTypeCellState(perType, type);
  const active = toPerTypeChannels(perType, type) ?? [];

  function toggle(channel: ChannelId) {
    const next = active.includes(channel)
      ? active.filter((c) => c !== channel)
      : [...active, channel];
    onChange(setPerTypeCell(perType, type, next));
  }

  return (
    <ChannelCell
      state={state}
      active={active}
      onToggle={toggle}
      onReset={() => onChange(setPerTypeCell(perType, type, undefined))}
    />
  );
}

/**
 * Notifiche — personal opt-out toggles for email / push (inApp is always on),
 * a per-type override grid (the real "overridable default" escape hatch), a
 * working "this device" push toggle + registered-device list, and, for the
 * tenant owner, the tenant notification gate.
 *
 * Persistence model:
 *  - Personal channel opt-outs write `notificationPreferences.channels.*` on
 *    the user's own record via the users API (same path PersonalSettings uses).
 *  - Personal per-type overrides write `notificationPreferences.perType` the
 *    same way: `undefined` for a type means "use whatever the tenant's
 *    (role × type) rules resolve to" (Task 9); `[]` means "off for me"; a
 *    non-empty array is a custom channel set that wins over the tenant default.
 *  - The "this device" toggle registers/revokes the FCM token for the current
 *    device via `enable/disablePushOnThisDevice`; the list reflects all of the
 *    user's registered devices live (`subscribeDevices`) and supports revoke.
 *  - The tenant gate (`TenantSettings.notifications`) is read from the reactive
 *    tenant settings and written by the owner via `updateTenantSettings`.
 */
export function NotificationsSettings() {
  const core = useCore();
  const t = core.i18n.t;
  const uid = core.auth.currentUserId();
  const isOwner = core.rbac.can("manage", "hotel");
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug?: string };

  const [prefs, setPrefs] = useState<NotificationPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<SaveStatus>("idle");

  // Device push state: null = unknown/idle, true = enabled on this device.
  const [devices, setDevices] = useState<ReadonlyArray<RegisteredDevice>>([]);
  const [currentToken, setCurrentToken] = useState<string | null>(null);
  const [deviceBusy, setDeviceBusy] = useState(false);
  const [deviceDenied, setDeviceDenied] = useState(false);
  const [deviceUnsupported, setDeviceUnsupported] = useState(false);
  const [deviceError, setDeviceError] = useState<string | null>(null);

  // Live list of the user's registered devices + the token of THIS device (so
  // the toggle reflects reality and "this device" can be marked / revoked).
  useEffect(() => {
    if (!uid || !tenantSlug) return;
    void currentDeviceToken().then(setCurrentToken);
    const unsub = subscribeDevices(tenantSlug, uid, setDevices);
    return () => unsub();
  }, [uid, tenantSlug]);

  const thisDeviceOn = currentToken !== null && devices.some((d) => d.token === currentToken);

  async function toggleDevice(enable: boolean): Promise<void> {
    if (!uid || !tenantSlug) return;
    setDeviceBusy(true);
    setDeviceDenied(false);
    setDeviceUnsupported(false);
    setDeviceError(null);
    try {
      if (enable) {
        const result = await enablePushOnThisDevice(tenantSlug, uid);
        if (result === "granted") setCurrentToken(await currentDeviceToken());
        else if (result === "denied") setDeviceDenied(true);
        // Surface "unsupported" so the toggle isn't a silent no-op (e.g. when
        // the VAPID key is unset or the browser can't do web push).
        else setDeviceUnsupported(true);
      } else {
        await disablePushOnThisDevice(tenantSlug, uid);
        setCurrentToken(null);
      }
    } catch (err) {
      setStatus("error");
      // Surface the real failure so push registration can be diagnosed on the
      // device (getToken / FCM subscription / Firestore write).
      const e = err as { code?: string; message?: string };
      setDeviceError(e.code ? `${e.code} — ${e.message ?? ""}` : (e.message ?? String(err)));
    } finally {
      setDeviceBusy(false);
    }
  }

  async function revokeDevice(token: string): Promise<void> {
    if (!uid || !tenantSlug) return;
    setDeviceBusy(true);
    try {
      await removeDevice(tenantSlug, uid, token, token === currentToken);
      if (token === currentToken) setCurrentToken(null);
    } catch {
      setStatus("error");
    } finally {
      setDeviceBusy(false);
    }
  }

  const initialTenant = (() => {
    try {
      return core.tenant.settings().notifications ?? null;
    } catch {
      return null;
    }
  })();
  const [tenantNotif, setTenantNotif] = useState<TenantNotif>(
    initialTenant ?? DEFAULT_TENANT_NOTIF,
  );
  const [tenantError, setTenantError] = useState<string | null>(null);
  // Synchronous re-entrancy guard: two rapid toggle clicks would otherwise each
  // capture `previous` at invocation time — the second captures the ALREADY
  // flipped value, so if its write fails its catch reverts to the wrong
  // pre-state (fail-unsafe for the email gate). A ref (not state) because the
  // guard must be seen synchronously within the same tick, before React
  // re-renders. Mirrors IntegrationRow's `busy` flag in IntegrationsSettings.
  const savingTenantRef = useRef(false);

  async function saveTenant(next: TenantNotif): Promise<void> {
    if (savingTenantRef.current) return;
    savingTenantRef.current = true;
    const previous = tenantNotif;
    setTenantNotif(next);
    setStatus("saving");
    setTenantError(null);
    try {
      const api = getUsersApi();
      const tok = await api.getAuthToken();
      // The server replaces `notifications` wholesale (mergeFields on the
      // whole field), so omitting `rules` here would silently delete
      // whatever the owner configured on the "Regole notifiche" admin page.
      // Read the current rules fresh (not from a mount-time snapshot) so a
      // concurrent rules edit isn't clobbered by this gate-only save.
      const currentRules = (() => {
        try {
          return core.tenant.settings().notifications?.rules;
        } catch {
          return undefined;
        }
      })();
      await updateTenantSettings(
        api.apiBase,
        { notifications: { ...next, ...(currentRules ? { rules: currentRules } : {}) } },
        tok,
      );
      setStatus("saved");
      window.setTimeout(() => setStatus("idle"), 1500);
    } catch (err) {
      // Revert the optimistic flip: the write never landed, so leaving the
      // switch on its new value would show e.g. Email as ON while the server
      // still has it OFF — fail-unsafe for the one control that keeps real
      // HR-imported addresses from getting automated mail.
      setTenantNotif(previous);
      setTenantError(
        err instanceof TenantSettingsUpdateError && err.code === "email_locked_governed_users"
          ? t("settings.notifications.tenant.emailLocked")
          : t("settings.error"),
      );
      setStatus("error");
    } finally {
      savingTenantRef.current = false;
    }
  }

  useEffect(() => {
    if (!uid) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      const api = getUsersApi();
      const tok = await api.getAuthToken();
      const { user } = (await getUser(api.apiBase, uid, tok)) as {
        user: { notificationPreferences?: NotificationPreferences };
      };
      if (!cancelled) setPrefs(mergeNotificationPreferences(user.notificationPreferences));
    })()
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [uid]);

  async function saveChannel(channel: "email" | "push", enabled: boolean): Promise<void> {
    if (!uid || !prefs) return;
    const next: NotificationPreferences = {
      ...prefs,
      channels: { ...prefs.channels, [channel]: enabled },
    };
    setPrefs(next);
    setStatus("saving");
    try {
      const api = getUsersApi();
      const tok = await api.getAuthToken();
      await updateUser(api.apiBase, uid, { notificationPreferences: next }, tok);
      setStatus("saved");
      window.setTimeout(() => setStatus("idle"), 1500);
    } catch {
      setStatus("error");
    }
  }

  async function savePerType(nextPerType: PerType): Promise<void> {
    if (!uid || !prefs) return;
    const next: NotificationPreferences = { ...prefs, perType: nextPerType };
    setPrefs(next);
    setStatus("saving");
    try {
      const api = getUsersApi();
      const tok = await api.getAuthToken();
      await updateUser(api.apiBase, uid, { notificationPreferences: next }, tok);
      setStatus("saved");
      window.setTimeout(() => setStatus("idle"), 1500);
    } catch {
      setStatus("error");
    }
  }

  if (loading) return <SkeletonFormSection fields={4} />;
  if (!prefs) return <p className="text-sm text-danger">{t("settings.error")}</p>;

  return (
    <div className="space-y-6">
      <Section width="readable">
        <div className="h-6 flex items-center">
          <StatusBadge status={status} />
        </div>
      </Section>

      <Section width="readable">
        <Card>
          <div className="mb-3 text-eyebrow">{t("settings.notifications.channels")}</div>
          <div className="space-y-1">
            <ToggleRow
              label={t("settings.notifications.inApp")}
              hint={t("settings.notifications.inApp.always")}
              checked
              disabled
            />
            <ToggleRow
              label={t("settings.notifications.email")}
              checked={prefs.channels.email}
              onChange={(c) => void saveChannel("email", c)}
            />
            <ToggleRow
              label={t("settings.notifications.push")}
              checked={prefs.channels.push}
              onChange={(c) => void saveChannel("push", c)}
            />
          </div>
        </Card>
      </Section>

      {/* Per-type override grid: same shape as the tenant-wide rules matrix
          in NotificationRulesSettings — a role/type × channel grid gains from
          width instead of losing to it, so it stays full width. */}
      <Section>
        <Card>
          <div className="mb-1 text-eyebrow">{t("settings.notifications.perType.title")}</div>
          <p className="mb-3 text-caption text-fg-muted">
            {t("settings.notifications.perType.description")}
          </p>
          <div className="space-y-6">
            {PERTYPE_AREAS.map((area) => (
              <div key={area}>
                <div className="mb-2 text-caption text-fg-muted">
                  {t(`settings.notifications.rules.area.${area}`)}
                </div>
                <div className="divide-y divide-border-default border-t border-border-default">
                  {CONFIGURABLE_NOTIFICATION_TYPES.filter((x) => x.area === area).map(
                    ({ type }) => (
                      <div
                        key={type}
                        className="flex flex-col gap-1.5 py-2.5 sm:flex-row sm:items-center sm:justify-between sm:gap-3"
                      >
                        <span className="text-sm text-fg-default">
                          {t(`settings.notifications.rules.type.${type}`)}
                        </span>
                        <PersonalTypeCell
                          perType={prefs.perType}
                          type={type}
                          onChange={(next) => void savePerType(next)}
                        />
                      </div>
                    ),
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      </Section>

      <Section width="readable">
        <Card>
          <div className="mb-3 text-eyebrow">{t("settings.notifications.device")}</div>
          <ToggleRow
            label={t("settings.notifications.device.enable")}
            {...(deviceDenied
              ? { hint: t("settings.notifications.device.denied") }
              : deviceUnsupported
                ? { hint: t("settings.notifications.device.unsupported") }
                : {})}
            checked={thisDeviceOn}
            disabled={deviceBusy}
            onChange={(c) => void toggleDevice(c)}
          />
          {deviceError && (
            <p className="mt-2 text-caption text-danger break-words">{deviceError}</p>
          )}

          <div className="mt-4 mb-1 text-eyebrow">{t("settings.notifications.device.list")}</div>
          {devices.length === 0 ? (
            <p className="text-caption text-fg-muted">{t("settings.notifications.device.none")}</p>
          ) : (
            <ul className="divide-y divide-border-default border-t border-border-default">
              {devices.map((d) => (
                <li
                  key={d.token}
                  className="flex items-center justify-between gap-3 py-2.5 min-h-touch"
                >
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className="text-sm text-fg-default truncate">
                        {deviceLabel(d.userAgent, t("settings.notifications.device.fallback"))}
                      </span>
                      {d.token === currentToken && (
                        <Badge tone="success" variant="soft" size="xs">
                          {t("settings.notifications.device.thisDevice")}
                        </Badge>
                      )}
                    </span>
                    <span className="block text-xs text-fg-muted">
                      {t("settings.notifications.device.activated")}{" "}
                      {relativeTime(timestampToMillis(d.createdAt), t)}
                    </span>
                  </span>
                  <button
                    type="button"
                    onClick={() => void revokeDevice(d.token)}
                    disabled={deviceBusy}
                    className="text-caption text-danger hover:underline min-h-touch px-2 disabled:opacity-40"
                  >
                    {t("settings.notifications.device.revoke")}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </Section>

      {isOwner && (
        <Section width="readable">
          <Card>
            <div className="mb-3 flex items-center gap-2">
              <span className="text-eyebrow">{t("settings.notifications.tenant")}</span>
              <Badge tone="warning" variant="outline" size="xs">
                {t("settings.tenant.ownerOnly")}
              </Badge>
            </div>
            <div className="space-y-1">
              <ToggleRow
                label={t("settings.notifications.tenant.enabled")}
                checked={tenantNotif.enabled}
                onChange={(c) => void saveTenant({ ...tenantNotif, enabled: c })}
              />
              <ToggleRow
                label={t("settings.notifications.email")}
                checked={tenantNotif.channels.email}
                onChange={(c) =>
                  void saveTenant({
                    ...tenantNotif,
                    channels: { ...tenantNotif.channels, email: c },
                  })
                }
              />
              <ToggleRow
                label={t("settings.notifications.push")}
                checked={tenantNotif.channels.push}
                onChange={(c) =>
                  void saveTenant({
                    ...tenantNotif,
                    channels: { ...tenantNotif.channels, push: c },
                  })
                }
              />
            </div>
            {tenantError && (
              <p className="mt-2 text-caption text-danger break-words">{tenantError}</p>
            )}
          </Card>
        </Section>
      )}
    </div>
  );
}
